/**
 * The face detector, loaded on the device and nowhere else (docs/5 § D260805-1206).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BlazeFace short-range, through `@tensorflow-models/face-detection` on the
 * TensorFlow.js WebGL backend. Apache-2.0, and the weights are committed under
 * `public/models/face-detector-short-v1/` — see the LICENSE.txt beside them for why
 * they are committed rather than fetched from Google.
 *
 * WHY ON THE DEVICE. The obvious place for this is the server, and the server
 * cannot have it: a Worker cannot decode an image inside its CPU budget, which
 * is already why the downscale is the client's job (docs/5 § D260804-2359). Paying a
 * vendor per check is the other option and it is the one this defers — but note
 * the property that falls out for free, because it is the better argument: a
 * photograph that fails these checks is never uploaded anywhere, so a frame of
 * somebody's dark bedroom is discarded on their phone rather than stored,
 * scanned and deleted on ours.
 *
 * WHAT THIS IS NOT. It is not liveness, not identity, and not verification —
 * `selfie_verified` (docs/2 § Verification) is a native, randomised-pose check
 * against a stored photograph and is unbuilt. This is a quality gate: light on a
 * face, a face at all, at a sensible distance. Nothing here should ever be cited
 * as evidence that the person in the frame is who they say they are, and no
 * column records its verdict.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * EVERYTHING IS IMPORTED DYNAMICALLY. TensorFlow.js and the model are roughly
 * half a megabyte between them, and they are needed on exactly one screen. A
 * static import would put them in the entry bundle for everybody who never opens
 * it — including the sign-in screen, which is the one screen whose speed decides
 * whether anybody sees the rest.
 */
import type { Face, FaceDetector } from '@tensorflow-models/face-detection';

export type { Face };

/**
 * Where the committed weights live. Root-relative, which is what works both on the
 * web and under Capacitor, where the app is served from `capacitor://localhost` and
 * a bare relative path resolves against the route.
 *
 * `-v1` IS A HASH BY HAND AND IT IS PAIRED WITH A HEADER. These bytes are unhashed
 * by Vite (they are `public/` assets, copied verbatim), so the only way the CDN can
 * cache them for a year is if the URL promises never to mean anything else — see the
 * `/models/face-detector-short-v1/*` rule in `public/_headers`. A different model is
 * a `-v2` directory and a new URL, never new bytes at this one. Moving one without
 * the other is how a stale model gets served for a year.
 */
const MODEL_URL = '/models/face-detector-short-v1/model.json';

/**
 * One load per session, shared by every caller.
 *
 * A PROMISE RATHER THAN THE DETECTOR, so that the live preview's first tick and
 * the shutter cannot start two loads of the same half-megabyte. The promise is
 * cached the moment it is created, not when it settles.
 */
let loading: Promise<FaceDetector | null> | null = null;

/**
 * The detector, or `null` if it cannot be had.
 *
 * `null` IS A SUPPORTED ANSWER AND NOT AN ERROR. No WebGL, a blocked model
 * fetch, an old WebView — all of them end here, and all of them mean the same
 * thing to the caller: send the photograph unchecked. Refusing to let somebody
 * set a photograph because our model would not load is a worse product than an
 * unchecked photograph, and it is a failure they could neither see nor fix.
 */
export function loadFaceDetector(): Promise<FaceDetector | null> {
  loading ??= start();
  return loading;
}

/**
 * Give the GPU everything back.
 *
 * WHAT IT RELEASES: the model's own tensors, which is the largest thing this module
 * allocates. `loading = null` means a later caller loads again rather than reaching
 * into a disposed detector — the retake dialog on the edit screen (docs/5 § D260805-0242)
 * mounts this component every time it is opened, so that path is ordinary, not
 * exceptional. Re-entry re-pays the load and the shader warmup, roughly 400ms, during
 * which the shutter works unchecked.
 *
 * WHAT IT DELIBERATELY DOES NOT DO IS REMOVE THE WebGL BACKEND, and that restraint
 * is the whole of this function's history. Reclaiming the context is the only way to
 * get back the ~13 MB of pooled textures and ~156 compiled programs measured after
 * twenty inferences — so an earlier version of this ended in
 * `tf.removeBackend('webgl')`, with a comment asserting the backend would re-register
 * on the next `setBackend` because importing the package is what registers it.
 *
 * **That comment was wrong, and the cost of it was the entire feature, silently.**
 * `removeBackend` unregisters the factory, and the ES module that registered it has
 * already been evaluated, so a second `import()` is a cache hit that re-runs nothing:
 * WebGL is gone for the life of the page. After one close-and-reopen of the retake
 * dialog, `createDetector` came back on a backend that no longer existed,
 * `estimateFaces` threw, `inspect` caught it and returned `unchecked` — and **every
 * photograph went through with no checks and nothing on screen to say so.** A gate
 * that is silently absent is worse than one that is visibly broken. Measured: first
 * open reported a fault correctly, the second and third reported `unchecked`.
 *
 * So the pooled textures are left to the page, and that is the right trade: a bounded
 * one-off cost against a check that keeps working every time somebody opens the
 * dialog. If that memory ever has to come back, it needs a way to re-register the
 * backend afterwards, and a test that opens the dialog twice.
 *
 * CALLED ONLY WHEN NO CHECK IS IN FLIGHT. Disposing mid-inference is a crash inside
 * tfjs, not a caught error, which is why the caller awaits its own last tick first.
 */
export async function disposeFaceDetector(): Promise<void> {
  const pending = loading;
  if (!pending) return;
  /* Cleared FIRST, so anything that calls in during the await below starts a fresh
     load rather than joining the one being torn down. */
  loading = null;
  try {
    const detector = await pending;
    /* Safe whoever else is loading: a detector owns the tensors IT allocated, and a
       newer one allocates its own. */
    detector?.dispose();
  } catch {
    /* A teardown that fails has nothing left to protect. */
  }
}

async function start(): Promise<FaceDetector | null> {
  try {
    /* The backend is registered by importing it, and it must be registered
       before `createDetector` asks tfjs for one — hence the await on `setBackend`
       rather than letting the first inference pick whatever is available. */
    const [tf, faceDetection] = await Promise.all([
      import('@tensorflow/tfjs-core'),
      import('@tensorflow-models/face-detection'),
      import('@tensorflow/tfjs-backend-webgl'),
    ]);

    await tf.setBackend('webgl');
    await tf.ready();

    const detector = await faceDetection.createDetector(
      faceDetection.SupportedModels.MediaPipeFaceDetector,
      {
        runtime: 'tfjs',
        /* Selfie range. `full` reaches five metres, costs more, and there is no
           such thing as a front-camera self-portrait taken from five metres. */
        modelType: 'short',
        /*
         * ONE FACE. The checks judge the photograph the person is taking of
         * themselves, so a second face in the frame is not a second subject to
         * choose between — and asking the model for more would mean writing a
         * rule about which one counts, which is a rule about who somebody is
         * allowed to be photographed beside.
         */
        maxFaces: 1,
        detectorModelUrl: MODEL_URL,
      },
    );

    /*
     * WARM IT UP HERE, ON A BLANK CANVAS. The first inference on the WebGL
     * backend compiles shaders and can take the better part of a second; paying
     * that during the load, while the camera is still opening, means the live
     * preview's first real tick is already fast. A failure is ignored on purpose:
     * a detector that cannot handle a blank canvas will report no face on it,
     * which is the correct answer anyway.
     */
    const warm = document.createElement('canvas');
    warm.width = 64;
    warm.height = 64;
    await detector.estimateFaces(warm).catch(() => []);

    return detector;
  } catch {
    /* Cached as a resolved `null` by the caller's `??=`, so a failed load is not
       retried on every frame of the live preview. */
    return null;
  }
}
